import type { LensCardData, LensFilterValues } from '~/types/leLens';

import { reportCalendar } from '../ContributionMargin/liveModel';

import { adgroupFields, dailyFields, salesDailyFields, salesSummaryFields } from './batchFields';

function decode(fields: Array<string>, rows: Array<Record<string, unknown>> = []): Array<Record<string, unknown>> {
  if (!rows.length) return [];
  // Existing saved definitions return ordinary rows until the new spec is registered.
  if (
    rows.every(
      (row) => !('payload' in row) && !('batch' in row) && !('source_count' in row) && !('record_count' in row),
    )
  )
    return rows;
  let expected: number | undefined;
  const seen = new Set<number>();
  const decoded = rows.flatMap((row) => {
    const batch = Number(row.batch),
      count = Number(row.record_count),
      total = Number(row.source_count);
    if (
      !Number.isSafeInteger(batch) ||
      batch < 0 ||
      seen.has(batch) ||
      !Number.isSafeInteger(count) ||
      count < 1 ||
      !Number.isSafeInteger(total) ||
      total < count ||
      (expected !== undefined && total !== expected)
    )
      throw Error('Invalid Performance Marketing evidence batch.');
    seen.add(batch);
    expected = total;
    if (typeof row.payload !== 'string') throw Error('Invalid Performance Marketing evidence payload.');
    const values: unknown = JSON.parse(row.payload);
    if (!Array.isArray(values) || values.length !== count)
      throw Error('Invalid Performance Marketing evidence length.');
    return values.map((value) => {
      if (!Array.isArray(value) || value.length !== fields.length)
        throw Error('Invalid Performance Marketing evidence record.');
      return Object.fromEntries(fields.map((field, index) => [field, value[index]]));
    });
  });
  if (decoded.length !== expected || [...seen].some((batch) => batch >= seen.size))
    throw Error('Incomplete Performance Marketing evidence; totals and CSV are withheld.');
  return decoded;
}

/** Expand only PM's complete grouped transport, before the existing ratios and CSV path. */
export function transformMarketingCard(card: LensCardData): LensCardData {
  const fieldsByCard: Record<string, Array<string>> = {
    adgroup: adgroupFields,
    daily: dailyFields,
    sales_daily: salesDailyFields,
    sales_summary: salesSummaryFields,
  };
  const fields = fieldsByCard[card.cardId];
  if (!fields) return card;
  if (card.truncated || card.comparison?.truncated)
    return {
      ...card,
      status: 'error',
      message: 'Incomplete Performance Marketing source or comparison transport.',
      rows: [],
      comparison: card.comparison ? { ...card.comparison, rows: [] } : undefined,
    };
  try {
    return {
      ...card,
      rows: decode(fields, card.rows),
      comparison: card.comparison ? { ...card.comparison, rows: decode(fields, card.comparison.rows) } : undefined,
    };
  } catch (error) {
    return {
      ...card,
      status: 'error',
      message: (error as Error).message,
      rows: [],
      comparison: card.comparison ? { ...card.comparison, rows: [] } : undefined,
    };
  }
}

/** Freeze the completed-date boundary in the same Sydney calendar as the physical source. */
export function completedMarketingRequest(request: LensFilterValues, now = new Date()): LensFilterValues {
  return { ...request, pm_as_of: reportCalendar(now).today };
}

/** Every card consumed by a PM view must be independently complete, including prior readings. */
export function marketingViewProblem(
  cards: Array<LensCardData>,
  view: string,
  activeCard: string,
  comparison: boolean,
): string | undefined {
  const auxiliary =
    view === 'sales' ? ['sales_daily', 'sales_total'] : ['performance', 'pop'].includes(view) ? ['daily'] : [];
  for (const id of [activeCard, ...auxiliary]) {
    const card = cards.find((item) => item.cardId === id);
    if (!card || !['ready', 'empty'].includes(card.status) || card.truncated)
      return `${id}: ${card?.message ?? (card?.truncated ? 'incomplete source result' : 'source data unavailable')}. Charts, totals and CSV are withheld.`;
    // The period chart consumes prior daily points; headline comparisons consume the active card.
    if (comparison && (id === activeCard || (view === 'pop' && id === 'daily'))) {
      const prior = card.comparison;
      if (!prior || !['ready', 'empty'].includes(prior.status) || prior.truncated)
        return `${id}: ${prior?.message ?? (prior?.truncated ? 'incomplete comparison result' : 'comparison data unavailable')}. Charts, totals and CSV are withheld.`;
    }
  }
  return undefined;
}
